import React, { useCallback, useEffect, useState } from 'react';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import DataTable from '../../ui/DataTable/DataTable';
import { CellText, CellId, CellNumber, CellMuted } from '../../ui/DataTable/cells';
import { Field, Input } from '../../ui/Field/Field';
import { finesService, errorMessage, officeError } from '../../services/finesService';

/**
 * Staff on Ejar (owner, 1 Oct 2026: "ممحكن نعمل فى سيتينج الادمن id الموظفين على ايجار اى حد يفتح فيهم عقد ميحسبش سالك
 * الدرهم وضريبته"). The company's own staff rent like customers but pay their Salik and Darb gates only - no AED 1 + VAT
 * a trip - on every contract of theirs, old and open. The engine (api/staff_trips) finds the contracts by the Ejar
 * customer id kept here, or the Emirates ID their contracts carry, and every page and report follows it at once.
 */
const digitsOf = (v) => String(v || '').replace(/\D/g, '');

const StaffCustomersPanel = () => {
    const [state, setState] = useState(null);          // the last good answer: {customers, ids, version, contracts, error}
    const [loadError, setLoadError] = useState('');
    const [value, setValue] = useState('');
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);

    // A failed read keeps what was shown and stops Add / Remove until a good read: a save is the WHOLE list, and one
    // built on an empty or stale list would drop the other staff (they would pay the trip fee again)
    const load = useCallback(async () => {
        try { setState(await finesService.staffCustomers()); setLoadError(''); } catch (e) { setLoadError(errorMessage(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const ready = !!state && !loadError;
    const ids = state?.ids || [];
    const save = async (next, done) => {
        if (!ready) return;
        setBusy(true); setNotice(null);
        try {
            // saved on the version this list was read with: someone else's save meanwhile is refused, never overwritten
            setState(await finesService.staffCustomersSave(next, state.version));
            setNotice(done);
        } catch (e) {
            const err = officeError(e);
            if (err.code === 'record-changed') {
                setNotice('Someone else changed the list just now - it is shown again as saved; make your change once more.');
                await load();
            } else setNotice(err.message);
        } finally { setBusy(false); }
    };
    const add = () => {
        const id = digitsOf(value);
        if (!id || !ready) return;
        if (ids.includes(id)) { setNotice(`${id} is on the list already`); return; }
        setValue('');
        save([...ids, id], `${id} added - their contracts lose the trip fee and the fines' 5 % fee now`);
    };
    const remove = (id) => save(ids.filter((x) => x !== id), `${id} removed - their trips carry AED 1 + VAT and their fines the 5 % fee again`);

    const columns = [
        { key: 'customerId', header: 'Ejar customer id / Emirates ID', cell: (r) => <CellId>{r.customerId}</CellId> },
        { key: 'name', header: 'Name', cell: (r) => (r.name ? <CellText>{r.name}</CellText> : <CellMuted>{r.found === false ? 'contracts not read yet' : 'not found on any contract'}</CellMuted>) },
        { key: 'identity', header: 'Emirates ID', cell: (r) => (r.identity ? <CellId>{r.identity}</CellId> : <CellMuted>—</CellMuted>) },
        { key: 'contracts', header: 'Contracts', cell: (r) => <CellNumber value={r.contracts || 0} /> },
        { key: 'open', header: 'Open now', cell: (r) => <CellNumber value={r.open || 0} /> },
        {
            key: 'remove', header: '', cell: (r) => (
                <Button size="sm" variant="ghost" disabled={busy || !ready} onClick={() => remove(r.customerId)}
                    leadingIcon={<Icon glyph={ICONS.close} size="sm" />} aria-label={`Remove ${r.customerId}`}>Remove</Button>
            ),
        },
    ];

    return (
        <div className="flex flex-col gap-4">
            <Card
                title="Staff on Ejar"
                subtitle="The company's own staff pay their Salik and Darb gates only - no AED 1 + VAT a trip - and their fines at the RTA amount - no 5 % fee + VAT (the AED 20 knowledge fee and the black points' service charge stay) - on every contract of theirs, old and open. Give the Ejar customer id (the number in the customer's Ejar page address) or the Emirates ID."
            >
                <div className="flex flex-wrap items-end gap-3">
                    <Field label="Ejar customer id or Emirates ID" className="flex-1 min-w-[240px]">
                        <Input value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()}
                            placeholder="168573624 · 784199058285299" className="w-full font-mono" />
                    </Field>
                    <Button variant="primary" onClick={add} disabled={busy || !ready || !digitsOf(value)} isLoading={busy}
                        leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>Add</Button>
                </div>
                {loadError && (
                    <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                        <span className="flex-1">The list could not be read: {loadError}. Adding and removing wait until it is read.</span>
                        <Button size="sm" variant="secondary" onClick={load} disabled={busy}>Try again</Button>
                    </div>
                )}
                {notice && <div className="mt-4 rounded-lg bg-gray-100 px-4 py-3 text-sm text-gray-800" role="status" aria-live="polite">{notice}</div>}
                {state?.error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">The engine could not read the contracts: {state.error}</div>}
            </Card>
            <DataTable
                title={`On the list · ${ids.length} · ${state?.contracts || 0} contract(s) with no trip fee`}
                columns={columns}
                data={state?.customers || []}
                rowKey={(r) => r.customerId}
                loading={!state}
                searchable={false}
                emptyTitle="No staff on the list yet."
                emptyIcon={ICONS.users}
            />
        </div>
    );
};

export default StaffCustomersPanel;
